import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { environment } from "../env";
import { createPortal } from "react-dom";
import { useFloorGames, useFloorImage } from "./api";
import { easeCubicInOut, featureColors, floorSvgMarkup, IDENTITY, placed, pointKey, transformAttr, zoomInTransform, zoomTween, type MapFeature, type Point, type Transform } from "./floor-logic";
import "./floor.css";

// THE ANGULAR `environment.egm_base_color` / `pulse_color`
const EGM_COLOR = environment.egmBaseColor;
const PULSE_COLOR = environment.pulseColor;

// ANGULAR TRUSTS THE FLOOR MARKUP OUTRIGHT (`safeHtml`). IT COMES FROM OUR OWN API, BUT IT IS
// STILL PARSED INERTLY AND STRIPPED OF SCRIPTS, EVENT HANDLERS AND `javascript:` LINKS FIRST
function sanitizeMarkup(markup: string): string {
  if (!markup || typeof DOMParser === "undefined") return "";
  const doc = new DOMParser().parseFromString(markup, "text/html");
  doc.querySelectorAll("script, foreignObject, iframe, object, embed").forEach((n) => n.remove());
  doc.body.querySelectorAll("*").forEach((el) => {
    for (const a of Array.from(el.attributes)) {
      const name = a.name.toLowerCase();
      if (name.startsWith("on") || (name.endsWith("href") && /^\s*javascript:/i.test(a.value))) el.removeAttribute(a.name);
    }
  });
  return doc.body.innerHTML;
}

// `ZoomIn` / `ZoomOut` TRANSITION LENGTH
const ZOOM_MS = 1500;

// PORT OF `floor-map`: THE FLOOR SVG FROM `/mf/floor/svg` (ITS `{{SVG_ID}}` REPLACED BY `svgId`),
// SIZED BY ITS VIEWBOX (`xMinYMin meet`) IN A CENTERED `svg-container` UP TO 1000PX WIDE, WITH A
// DOT PER PLACED EGM / TABLE GAME DRAWN INTO ITS FIRST `<g>`. ACTIVE GAMES (`features`) ARE
// RECOLORED AND RIPPLE; THE SELECTED MACHINE (`pulse`) GETS A `pulse_color` PULSE. `zoomTo`
// ANIMATES THE `<g>` TO 4X CENTERED ON THAT POINT, AND BACK TO THE WHOLE FLOOR WHEN NULL, WITH
// D3'S OWN ZOOM TRANSITION. AS IN ANGULAR, THE USER CANNOT PAN OR ZOOM BY HAND (ITS D3 ZOOM
// FILTER `() => this.isZooming` IS ALWAYS FALSE); ONLY THE PAGE MOVES THE VIEW.
export function FloorMap(props: { svgId: string; features?: readonly MapFeature[]; pulse?: Point | null; zoomTo?: Point | null }) {
  const { svgId, features = [], pulse = null, zoomTo = null } = props;
  const image = useFloorImage();
  const games = useFloorGames();
  const html = useMemo(() => sanitizeMarkup(floorSvgMarkup(image.data, svgId)), [image.data, svgId]);
  const width = image.data?.width ?? 0;
  const height = image.data?.height ?? 0;
  const radius = image.data?.machineRadius ?? 1;

  const hostRef = useRef<HTMLDivElement>(null);
  const [layer, setLayer] = useState<SVGGElement | null>(null);

  // `setupZoom()`: ONCE THE MARKUP IS IN THE DOM, GIVE THE SVG ITS VIEWBOX AND FIND THE LAYER
  // THE DOTS AND THE ZOOM TRANSFORM GO ON (ANGULAR WAITS 300MS FOR THIS; A LAYOUT EFFECT RUNS
  // RIGHT AFTER THE MARKUP IS COMMITTED)
  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host || !html) {
      setLayer(null);
      return;
    }
    const svg = host.querySelector<SVGSVGElement>(`#${CSS.escape(svgId)}`) ?? host.querySelector("svg");
    if (!svg) {
      setLayer(null);
      return;
    }
    svg.setAttribute("preserveAspectRatio", "xMinYMin meet");
    svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
    svg.classList.add("svg-content");
    setLayer(svg.querySelector("g"));
  }, [html, svgId, width, height]);

  // THE ZOOM TRANSITION. `current` IS WHERE THE LAYER IS NOW, SO A NEW ZOOM STARTED MID-FLIGHT
  // CONTINUES FROM WHERE THE LAST ONE LEFT OFF, AS AN INTERRUPTED D3 TRANSITION DOES
  const current = useRef<Transform>(IDENTITY);
  const zx = zoomTo?.x;
  const zy = zoomTo?.y;
  useEffect(() => {
    if (!layer || !width || !height) return;
    const target = zx === undefined || zy === undefined ? IDENTITY : zoomInTransform(width, height, { x: zx, y: zy });
    const from = current.current;
    if (from.k === target.k && from.x === target.x && from.y === target.y) return;
    const tween = zoomTween(from, target, width, height);
    const start = performance.now();
    let frame = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / ZOOM_MS);
      const tr = tween(easeCubicInOut(t));
      current.current = tr;
      layer.setAttribute("transform", transformAttr(tr));
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [layer, width, height, zx, zy]);

  const colors = featureColors(features);
  const dots = (games.data ?? []).filter(placed);

  return (
    <div className="pillars-floor-map parent-svg-container">
      <div
        ref={hostRef}
        className="svg-container"
        style={{ visibility: layer ? "visible" : "hidden" }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {layer &&
        createPortal(
          <>
            {dots.map((g, i) => {
              const color = colors.get(pointKey(g.x, g.y));
              return (
                <circle
                  key={`${g.location ?? ""}-${i}`}
                  className={color ? "egm transition pillars-floor-feature" : "egm"}
                  cx={g.x}
                  cy={g.y}
                  r={radius}
                  fill={color ?? EGM_COLOR}
                  stroke={color}
                />
              );
            })}
            {pulse && (
              <circle key={pointKey(pulse.x, pulse.y)} id="pulse_circle" className="pillars-floor-pulse" cx={pulse.x} cy={pulse.y} r={radius} stroke={PULSE_COLOR} fill={PULSE_COLOR} />
            )}
          </>,
          layer,
        )}
    </div>
  );
}
